import {useMemo} from 'react'
import {View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'
import {countGraphemes} from 'unicode-segmenter/grapheme'

import {useProfileQuery} from '#/state/queries/profile'
import {useSession} from '#/state/session'
import {
  STARTER_PACK_DESCRIPTION_MAX_GRAPHEMES,
  STARTER_PACK_NAME_MAX_GRAPHEMES,
  useWizardState,
} from '#/screens/StarterPack/Wizard/State'
import {atoms as a, useTheme} from '#/alf'
import * as TextField from '#/components/forms/TextField'
import {ScreenTransition} from '#/components/ScreenTransition'
import {EntourageAvatars} from '#/components/StarterPack/EntourageAvatars'
import {Text} from '#/components/Typography'

export function StepDetails() {
  const {_} = useLingui()
  const t = useTheme()
  const [state, dispatch] = useWizardState()

  const {currentAccount} = useSession()
  const {data: currentProfile} = useProfileQuery({
    did: currentAccount?.did,
    staleTime: Infinity,
  })

  const name = currentProfile?.displayName || currentProfile?.handle

  const profiles = useMemo(() => {
    const seen = new Set<string>()
    return [...(currentProfile ? [currentProfile] : []), ...state.profiles]
      .filter(p => {
        if (seen.has(p.did)) return false
        seen.add(p.did)
        return true
      })
      .slice(0, 4)
  }, [currentProfile, state.profiles])

  return (
    <ScreenTransition direction={state.transitionDirection} enabledWeb>
      <View style={[a.px_xl, a.gap_xl, a.mt_4xl]}>
        <View style={[a.gap_md, a.align_center, a.px_md, a.mb_md]}>
          <EntourageAvatars profiles={profiles} size={90} />
          <Text style={[a.font_semi_bold, a.text_3xl]}>
            <Trans>Invites, but personal</Trans>
          </Text>
          <Text style={[a.text_center, a.text_md, a.px_md]}>
            <Trans>
              Invite your friends to follow your favorite feeds and people
            </Trans>
          </Text>
        </View>
        <View>
          <TextField.LabelText>
            <Trans>What do you want to call your Entourage?</Trans>
          </TextField.LabelText>
          <TextField.Root>
            <TextField.Input
              label={
                name ? _(msg`${name}'s Entourage`) : _(msg`My Entourage`)
              }
              value={state.name}
              onChangeText={text => dispatch({type: 'SetName', name: text})}
            />
            <TextField.SuffixText
              label={_(
                msg({
                  comment:
                    'Accessibility label describing how many characters the user has entered out of a 50-character limit in a text input field',
                  message: `${countGraphemes(state.name ?? '')} out of 50`,
                }),
              )}>
              <Text style={[t.atoms.text_contrast_medium]}>
                {countGraphemes(state.name ?? '')}/
                {STARTER_PACK_NAME_MAX_GRAPHEMES}
              </Text>
            </TextField.SuffixText>
          </TextField.Root>
        </View>
        <View>
          <TextField.LabelText>
            <Trans>Tell us a little more</Trans>
          </TextField.LabelText>
          <TextField.Root>
            <TextField.Input
              label={
                name
                  ? _(msg`${name}'s favorite feeds and people - join me!`)
                  : _(msg`My favorite feeds and people - join me!`)
              }
              value={state.description}
              onChangeText={text =>
                dispatch({type: 'SetDescription', description: text})
              }
              multiline
              style={{minHeight: 150}}
            />
          </TextField.Root>
          <Text
            style={[
              a.text_right,
              a.text_xs,
              a.mt_xs,
              t.atoms.text_contrast_medium,
            ]}>
            {countGraphemes(state.description ?? '')}/
            {STARTER_PACK_DESCRIPTION_MAX_GRAPHEMES}
          </Text>
        </View>
      </View>
    </ScreenTransition>
  )
}
